import { lazy } from 'react';
import { Route, Routes, useParams } from 'react-router';
import { ServerRedirect } from '../../components';
import { retiredArticles } from '../Article/retiredArticles';
import { getFocusModel } from '../BeogramEntity/focusModels';

// Pages are split into their own chunks so each route only downloads what it
// renders (e.g. Swiper and the USA 2010 page stay out of the Services bundle).
const Article = lazy(async () => ({ default: (await import('../Article')).Article }));
const Articles = lazy(async () => ({ default: (await import('../Articles')).Articles }));
const BeogramEntity = lazy(async () => ({ default: (await import('../BeogramEntity')).BeogramEntity }));
const Contact = lazy(async () => ({ default: (await import('../Contact')).Contact }));
const Home = lazy(async () => ({ default: (await import('../Home')).Home }));
const Parts = lazy(async () => ({ default: (await import('../Parts')).Parts }));
const LegacyPartRedirect = lazy(async () => ({ default: (await import('../Product')).LegacyPartRedirect }));
const Product = lazy(async () => ({ default: (await import('../Product')).Product }));
const StandalonePart = lazy(async () => ({ default: (await import('../Product')).StandalonePart }));
const OtherModels = lazy(async () => ({ default: (await import('../OtherModels')).OtherModels }));
const PageNotFound = lazy(async () => ({ default: (await import('../PageNotFound')).PageNotFound }));
const Photography = lazy(async () => ({ default: (await import('../Photography')).Photography }));
const Sell = lazy(async () => ({ default: (await import('../Sell')).Sell }));
const Services = lazy(async () => ({ default: (await import('../Services')).Services }));
const USA = lazy(async () => ({ default: (await import('../Photography/USA')).USA }));
const USA2010 = lazy(async () => ({ default: (await import('../USA_2010')).USA2010 }));

const resetScroll = (): void => {
  if (typeof window !== 'undefined') {
    window.scrollTo(0, 0);
  }
};

export const appRoutes = (
  <Routes>
    <Route
      path='/'
      Component={ () => {
        resetScroll();

        return (
          <Home />
        );
      } } />

    <Route
      path='/articles'
      Component={ () => {
        resetScroll();

        return (
          <Articles />
        );
      } } />

    <Route
      path='/blog'
      element={ (
        <ServerRedirect to={ '/articles' } />
      ) } />

    <Route
      path='/articles/:slug'
      Component={ () => {
        const { slug } = useParams();
        const retiredTo = retiredArticles[slug ?? ''];

        if (retiredTo) {
          return (
            <ServerRedirect to={ retiredTo } />
          );
        }

        resetScroll();

        return (
          <Article slug={ slug ?? '' } />
        );
      } } />

    <Route
      path='/blog/:slug'
      Component={ () => {
        const { slug } = useParams();

        resetScroll();

        return (
          <ServerRedirect to={ retiredArticles[slug ?? ''] ?? `/articles/${ slug }` } />
        );
      } } />

    <Route
      path='/photography/landscape-and-nature'
      Component={ () => {
        resetScroll();

        return (
          <Photography />
        );
      } } />

    <Route
      path='/photography/united-states-of-america'
      Component={ () => {
        resetScroll();

        return (
          <USA />
        );
      } } />

    <Route
      path='/usa_2010'
      Component={ () => {
        resetScroll();

        return (
          <USA2010 />
        );
      } } />

    <Route
      path='/other-models'
      Component={ () => {
        resetScroll();

        return (
          <OtherModels />
        );
      } } />

    <Route
      path='/parts'
      Component={ () => {
        resetScroll();

        return (
          <Parts />
        );
      } } />

    <Route
      path='/parts/:slug'
      Component={ () => {
        const { slug } = useParams();

        resetScroll();

        return (
          <Product slug={ slug ?? '' } />
        );
      } } />

    {/* Parts without a product of their own. */}
    <Route
      path='/parts/pcb/:id'
      Component={ () => {
        const { id } = useParams();

        resetScroll();

        return (
          <StandalonePart
            kind='pcb'
            id={ Number(id) } />
        );
      } } />

    <Route
      path='/parts/3d/:id'
      Component={ () => {
        const { id } = useParams();

        resetScroll();

        return (
          <StandalonePart
            kind='print'
            id={ Number(id) } />
        );
      } } />

    {/* The PCB and 3D model lists were merged into the Parts catalogue. */}
    <Route
      path='/projects/beo-pcb'
      element={ (
        <ServerRedirect to={ '/parts?type=pcb' } />
      ) } />

    <Route
      path='/projects/3d-models'
      element={ (
        <ServerRedirect to={ '/parts?type=3d' } />
      ) } />

    {/* Every part now has its page under /parts. */}
    <Route
      path='/projects/3d-models/:id'
      Component={ () => {
        const { id } = useParams();

        return (
          <LegacyPartRedirect
            kind='print'
            id={ Number(id) } />
        );
      } } />

    <Route
      path='/contact'
      Component={ () => {
        resetScroll();

        return (
          <Contact />
        );
      } } />

    <Route
      path='/restoration-services'
      Component={ () => {
        resetScroll();

        return (
          <Services />
        );
      } } />

    <Route
      path='/sell-your-bang-olufsen'
      Component={ () => {
        resetScroll();

        return (
          <Sell />
        );
      } } />

    <Route
      path='/beovox/:id'
      Component={ () => {
        const { id } = useParams();

        return (
          <ServerRedirect to={ `/beovox/${ id }/articles` } />
        );
      } } />

    <Route
      path='/tools/:id'
      Component={ () => {
        const { id } = useParams();

        return (
          <ServerRedirect to={ `/tools/${ id }/articles` } />
        );
      } } />

    <Route
      path='/beomaster/:id'
      Component={ () => {
        const { id } = useParams();

        return (
          <ServerRedirect to={ `/beomaster/${ id }/articles` } />
        );
      } } />

    <Route
      path='/beovox/:id/:tabId'
      Component={ () => {
        const { id, tabId } = useParams();

        resetScroll();

        return (
          <BeogramEntity
            id={ id ?? '' }
            tab={ tabId ?? '' }
            prefix={ 'beovox' } />
        );
      } } />

    <Route
      path='/tools/:id/:tabId'
      Component={ () => {
        const { id, tabId } = useParams();

        resetScroll();

        return (
          <BeogramEntity
            id={ id ?? '' }
            tab={ tabId ?? '' }
            prefix={ 'tools' } />
        );
      } } />

    <Route
      path='/beogram/:id'
      Component={ () => {
        const { id } = useParams();

        // The focus models have an overview page; other models go to their repairs and upgrades.
        if (getFocusModel('beogram', id ?? '')) {
          resetScroll();

          return (
            <BeogramEntity
              id={ id ?? '' }
              tab={ 'overview' }
              prefix={ 'beogram' } />
          );
        }

        return (
          <ServerRedirect to={ `/beogram/${ id }/articles` } />
        );
      } } />

    <Route
      path='/beogram/:id/:tabId'
      Component={ () => {
        const { id, tabId } = useParams();

        resetScroll();

        return (
          <BeogramEntity
            id={ id ?? '' }
            tab={ tabId ?? '' }
            prefix={ 'beogram' } />
        );
      } } />

    <Route
      path='/beomaster/:id/:tabId'
      Component={ () => {
        const { id, tabId } = useParams();

        resetScroll();

        return (
          <BeogramEntity
            id={ id ?? '' }
            tab={ tabId ?? '' }
            prefix={ 'beomaster' } />
        );
      } } />

    <Route
      path='/beolink/:id'
      Component={ () => {
        const { id } = useParams();

        // The focus models have an overview page; other models go to their repairs and upgrades.
        if (getFocusModel('beolink', id ?? '')) {
          resetScroll();

          return (
            <BeogramEntity
              id={ id ?? '' }
              tab={ 'overview' }
              prefix={ 'beolink' } />
          );
        }

        return (
          <ServerRedirect to={ `/beolink/${ id }/articles` } />
        );
      } } />

    <Route
      path='/beolink/:id/:tabId'
      Component={ () => {
        const { id, tabId } = useParams();

        resetScroll();

        return (
          <BeogramEntity
            id={ id ?? '' }
            tab={ tabId ?? '' }
            prefix={ 'beolink' } />
        );
      } } />

    <Route
      path='/projects/pcb/:id'
      Component={ () => {
        const { id } = useParams();

        return (
          <LegacyPartRedirect
            kind='pcb'
            id={ Number(id) } />
        );
      } } />

    <Route
      path='/*'
      element={ (
        <PageNotFound />
      ) } />
  </Routes>
);
